/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useState } from 'react';
import { Card, Form, Input, Select, DatePicker, Button, Typography, Row, Col, message } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { PROVINCE_OPTIONS } from '../../constants';

const { Title } = Typography;

export interface CustomFilterValues {
  profileTypes?: string[];
  searchName?: string;
  searchPhone?: string;
  searchEmail?: string;
  searchUserId?: string;
  genders?: string[];
  provinces?: string[];
  ageGroups?: string[];
  dobFrom?: string;
  dobTo?: string;
  createdFrom?: string;
  createdTo?: string;
}

interface ProfileCustomFilterProps {
  target?: 'user' | 'relative' | 'all';
  onSearch: (values: CustomFilterValues) => void;
  onReset: () => void;
  loading?: boolean;
}

const AGE_GROUPS_OPTIONS = [
  { value: 'AGE_0_7M', label: 'Từ 0 - dưới 7 tháng tuổi' },
  { value: 'AGE_7_9M', label: 'Từ 7 – dưới 9 tháng tuổi' },
  { value: 'AGE_9_12M', label: 'Từ 9 – dưới 12 tháng tuổi' },
  { value: 'AGE_12_18M', label: 'Từ 12 – dưới 18 tháng tuổi' },
  { value: 'AGE_18_24M', label: 'Từ 18 – dưới 24 tháng tuổi' },
  { value: 'AGE_24_36M', label: 'Từ 24 – dưới 36 tháng tuổi' },
  { value: 'AGE_36_60M', label: 'Từ 36 – dưới 60 tháng tuổi' },
  { value: 'AGE_60_72M', label: 'Từ 60 – dưới 72 tháng tuổi' },
  { value: 'AGE_72M_8Y', label: 'Từ 72 tháng – dưới 8 tuổi' },
  { value: 'AGE_8_10Y', label: 'Từ 8 – dưới 10 tuổi' },
  { value: 'AGE_10_12Y', label: 'Từ 10 – dưới 12 tuổi' },
  { value: 'AGE_12_15Y', label: 'Từ 12 – dưới 15 tuổi' },
  { value: 'AGE_15_19Y', label: 'Từ 15 – tròn 19 tuổi' },
  { value: 'AGE_19_20Y', label: 'Từ trên 19 tuổi - dưới 20 tuổi' },
  { value: 'AGE_20_30Y', label: 'Từ 20 - dưới 30 tuổi' },
  { value: 'AGE_30Y', label: '30 tuổi' },
  { value: 'AGE_31_50Y', label: 'Từ 31 - dưới 50 tuổi' },
  { value: 'AGE_50_70Y', label: 'Từ 50 - dưới 70 tuổi' },
  { value: 'AGE_GE_70Y', label: 'Từ ≥70 tuổi' },
  { value: 'NO_INFO', label: 'Không có thông tin' },
];

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Nam' },
  { value: 'FEMALE', label: 'Nữ' },
  { value: 'NO_INFO', label: 'Không có thông tin' },
];

export const ProfileCustomFilter: React.FC<ProfileCustomFilterProps> = ({
  target = 'user',
  onSearch,
  onReset,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [dobRange, setDobRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [createdRange, setCreatedRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  const handleFinish = (values: any) => {
    for (const range of [dobRange, createdRange]) {
      if (range[0] && range[1] && range[0].isAfter(range[1], 'day')) {
        message.error('Từ ngày phải nhỏ hơn hoặc bằng Đến ngày.');
        return;
      }
    }
    const filterData: CustomFilterValues = {
      profileTypes: values.profileTypes,
      searchName: values.searchName,
      searchPhone: target === 'relative' ? undefined : values.searchPhone,
      searchEmail: target === 'relative' ? undefined : values.searchEmail,
      searchUserId: values.searchUserId,
      genders: values.genders,
      provinces: values.provinces,
      ageGroups: values.ageGroups,
      dobFrom: dobRange[0] ? dobRange[0].format('YYYY-MM-DD') : undefined,
      dobTo: dobRange[1] ? dobRange[1].format('YYYY-MM-DD') : undefined,
      createdFrom: createdRange[0] ? createdRange[0].format('YYYY-MM-DD') : undefined,
      createdTo: createdRange[1] ? createdRange[1].format('YYYY-MM-DD') : undefined,
    };
    onSearch(filterData);
  };

  const handleResetForm = () => {
    form.resetFields();
    setDobRange([null, null]);
    setCreatedRange([null, null]);
    onReset();
  };

  const searchNameItem = (
    <Form.Item name="searchName" label="Họ và tên" labelAlign="left">
      <Input placeholder="Nhập họ và tên" allowClear />
    </Form.Item>
  );

  const searchEmailItem = (
    <Form.Item name="searchEmail" label="Email" labelAlign="left">
      <Input placeholder="Nhập email" allowClear />
    </Form.Item>
  );

  const gendersItem = (
    <Form.Item name="genders" label="Giới tính" labelAlign="left">
      <Select
        mode="multiple"
        allowClear
        placeholder="Chọn giới tính"
        options={GENDER_OPTIONS}
        maxTagCount="responsive"
      />
    </Form.Item>
  );

  const ageGroupsItem = (
    <Form.Item name="ageGroups" label="Độ tuổi" labelAlign="left">
      <Select
        mode="multiple"
        allowClear
        placeholder="Chọn độ tuổi"
        options={AGE_GROUPS_OPTIONS}
        maxTagCount="responsive"
      />
    </Form.Item>
  );

  const searchUserIdItem = (label: string) => (
    <Form.Item name="searchUserId" label={label} labelAlign="left">
      <Input placeholder="Nhập User ID (ngăn cách bằng ;)" allowClear />
    </Form.Item>
  );

  const searchPhoneItem = (
    <Form.Item name="searchPhone" label="Số điện thoại" labelAlign="left">
      <Input placeholder="Nhập số điện thoại" allowClear />
    </Form.Item>
  );

  const dobItem = (
    <Form.Item label="Ngày sinh" labelAlign="left">
      <div css={dateRangeStyle}>
        <DatePicker
          placeholder="Từ ngày"
          format="DD/MM/YYYY"
          value={dobRange[0]}
          onChange={date => setDobRange([date, dobRange[1]])}
          style={{ width: '48%' }}
        />
        <span style={{ margin: '0 4px' }}>-</span>
        <DatePicker
          placeholder="Đến ngày"
          format="DD/MM/YYYY"
          value={dobRange[1]}
          onChange={date => setDobRange([dobRange[0], date])}
          style={{ width: '48%' }}
        />
      </div>
    </Form.Item>
  );

  const provincesItem = (
    <Form.Item name="provinces" label="Tỉnh thành" labelAlign="left">
      <Select
        mode="multiple"
        allowClear
        placeholder="Chọn tỉnh thành"
        options={PROVINCE_OPTIONS}
        maxTagCount="responsive"
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
      />
    </Form.Item>
  );

  const createdItem = (
    <Form.Item label="Thời gian tạo hồ sơ" labelAlign="left">
      <div css={dateRangeStyle}>
        <DatePicker
          placeholder="Từ ngày"
          format="DD/MM/YYYY"
          value={createdRange[0]}
          onChange={date => setCreatedRange([date, createdRange[1]])}
          style={{ width: '48%' }}
        />
        <span style={{ margin: '0 4px' }}>-</span>
        <DatePicker
          placeholder="Đến ngày"
          format="DD/MM/YYYY"
          value={createdRange[1]}
          onChange={date => setCreatedRange([createdRange[0], date])}
          style={{ width: '48%' }}
        />
      </div>
    </Form.Item>
  );

  return (
    <Card css={cardStyle}>
      <Title level={4} style={{ marginBottom: 24 }}>
        Danh sách tuỳ chỉnh
      </Title>

      <Form form={form} layout="horizontal" labelAlign="left" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} onFinish={handleFinish} style={{ padding: '0 24px' }}>
        <Row gutter={32}>
          {/* Left Column */}
          <Col span={12}>
            {target === 'user' && (
              <>
                {searchNameItem}
                {searchEmailItem}
                {gendersItem}
                {ageGroupsItem}
                {searchUserIdItem('User ID')}
              </>
            )}
            {target === 'relative' && (
              <>
                {searchNameItem}
                {gendersItem}
                {searchUserIdItem('User ID Người tạo hồ sơ')}
              </>
            )}
            {target === 'all' && (
              <>
                {searchNameItem}
                {gendersItem}
                {ageGroupsItem}
              </>
            )}
          </Col>

          {/* Right Column */}
          <Col span={12}>
            {target === 'user' && (
              <>
                {searchPhoneItem}
                {dobItem}
                {provincesItem}
                {createdItem}
              </>
            )}
            {target === 'relative' && (
              <>
                {dobItem}
                {ageGroupsItem}
                {createdItem}
              </>
            )}
            {target === 'all' && (
              <>
                {dobItem}
                {createdItem}
              </>
            )}
          </Col>
        </Row>

        {/* Buttons Centered at Bottom */}
        <div css={buttonGroupStyle}>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleResetForm}
            style={{ borderRadius: 6, minWidth: 100, marginRight: 12 }}
          >
            Đặt lại
          </Button>

          <Button
            type="primary"
            htmlType="submit"
            icon={<SearchOutlined />}
            loading={loading}
            style={{ borderRadius: 6, minWidth: 110 }}
          >
            Tìm kiếm
          </Button>
        </div>
      </Form>
    </Card>
  );
};

const cardStyle = css`
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
`;

const dateRangeStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const buttonGroupStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
`;
